import {
  ActionType,
  CounterType,
  formatDateToMMDDYYYY,
  Issue,
  IssueType,
  parseMMDDYYYYToDate,
  RoutineType,
  Severity,
  useCreateMutation,
  useFindByQuery,
  useUpdateIssue,
  useUpdateMutation,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { mixed, string, object as yupObject } from 'yup';
import { useAutoFocusRef } from '../../hooks';
import { Spacing } from '../../styles';
import { formStyles } from '../../styles/forms';
import { Colors } from '../../themes/colors';
import { Button } from '../crop-circle/Button';
import { CardButton } from '../crop-circle/CardButton';
import { Toggle } from '../crop-circle/Components/Toggle/Toggle';
import { TypeIcon, TypeVariant } from '../crop-circle/Components/Type';
import { Icon } from '../crop-circle/icons';
import { colors, tokens } from '../crop-circle/tokens';
import {
  ControlledTextInput,
  DatePickerSelector,
  SaveFormBottom,
} from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { AdjustCreationDateField } from './adjustCreationDateField';
import { CreatedDateField } from './createdDateField';
import { DeleteIssue } from './deleteIssue';
import { LabelSelector } from './labelSelector';

type IssueEditModalProps = {
  issueId: string;
  currentTitle: string;
  workspaceId?: string;
  currentLabels?: Array<{ id: string; name: string }>;
  /** Controlled labels driven by an external state owner (e.g. third panel). Set to undefined to use uncontrolled mode. */
  externalLabels?: Array<{ id: string; name: string }>;
  /** Called whenever labels change internally so parent can keep its state in sync. */
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
  currentAdjustCreationDate?: string;
  currentCreatedAt?: string;
  issue: Issue;
  onLabelSelectorEdit?: () => void;
  bottomBar: React.ComponentType<Record<string, unknown>>;
  onReassignPress?: () => void;
  onEditCancel?: () => void;
  /** Called when form dirty/valid state changes, exposing save handler for parent-level navigation guards */
  onFormStateChange?: (
    isDirty: boolean,
    isValid: boolean,
    onSave: () => Promise<void>
  ) => void;
  onDeleted?: () => Promise<void>;
  onSaved?: () => void;
  onReviseSubmissionPress?: () => void;
  /** Optional node rendered at the end of the scroll content (web only, after form fields). */
  contentFooter?: React.ReactNode;
  /**
   * Campaign Issues render read-only by default. When true, pin-to-asset,
   * reminder and labels stay editable even for campaign Issues — the campaign
   * thread "settings" panel opts in and supplies its own save bar. Title, type,
   * reassign and adjust-creation-date remain campaign read-only.
   */
  allowCampaignMetadataEdit?: boolean;
};

type IssueEditFormData = {
  title: string;
  adjustCreationDate?: unknown;
  onReminderDate?: unknown;
};

export const IssueEdit: React.FC<IssueEditModalProps> = (
  props: IssueEditModalProps
) => {
  const { t } = useTranslation();

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': props.issueId },
    };
  }, [props.issueId]);

  const { data: workflows } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: updateIssue } = useUpdateIssue();
  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const [currentWorkflow, setCurrentWorkflow] = useState(
    workflows?.items?.[0] || null
  );

  const { labels: availableLabels } = useWorkspaceLabels(props.workspaceId);

  const formSchema = yupObject({
    title: string().required(),
    adjustCreationDate: mixed().optional(),
    onReminderDate: mixed().optional(),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      title: props.currentTitle ?? '',
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
      onReminderDate: currentWorkflow?.trigger?.config?.onDate
        ? formatDateToMMDDYYYY(currentWorkflow.trigger.config.onDate)
        : undefined,
    },
  });

  useEffect(() => {
    const workflow = workflows?.items?.[0] || null;
    if (workflow && workflow !== currentWorkflow) {
      setCurrentWorkflow(workflow);
      if (workflow?.trigger?.config?.onDate) {
        setValue(
          'onReminderDate',
          formatDateToMMDDYYYY(workflow.trigger.config.onDate)
        );
      }
    }
  }, [workflows, currentWorkflow, setValue]);

  const onSubmit = async (data: IssueEditFormData) => {
    const input = {
      id: props.issueId,
      title: data.title,
      severity: selectedSeverity,
      labels: selectedLabels.map((l) => ({ id: l.id })),
      adjustCreationDate: data.adjustCreationDate
        ? parseMMDDYYYYToDate(String(data.adjustCreationDate))
        : undefined,
      ...(pinChanged && { isPinned }),
    };

    await updateIssue({ input: input });

    if (typeof data.onReminderDate === 'string' && data.onReminderDate) {
      await createOrUpdateReminder(data.onReminderDate);
    }

    setLabelsChanged(false);
    setSeverityChanged(false);
    setPinChanged(false);
    props.onSaved?.();
  };

  const onEditCancel = () => {
    reset({
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    setSelectedSeverity(props.issue?.severity ?? Severity.Red);
    setSeverityChanged(false);
    setIsPinned(props.issue?.isPinned ?? false);
    setPinChanged(false);
    props.onLabelsChange?.(props.currentLabels || []);
    props.onEditCancel && props.onEditCancel();
  };

  const elementFocusCallBack = useAutoFocusRef();
  const [selectedLabels, setSelectedLabels] = React.useState<
    Array<{ id: string; name: string }>
  >(props.currentLabels || []);
  const [labelsChanged, setLabelsChanged] = React.useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity>(
    props.issue?.severity ?? Severity.Red
  );
  const [severityChanged, setSeverityChanged] = useState(false);
  const [isPinned, setIsPinned] = useState(props.issue?.isPinned ?? false);
  const [pinChanged, setPinChanged] = useState(false);

  useEffect(() => {
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
  }, [props.issueId, props.currentLabels]);

  useEffect(() => {
    if (props.externalLabels === undefined) return;
    setSelectedLabels(props.externalLabels);
    const serverIds = (props.currentLabels || [])
      .map((l) => l.id)
      .sort()
      .join(',');
    const externalIds = props.externalLabels
      .map((l) => l.id)
      .sort()
      .join(',');
    setLabelsChanged(serverIds !== externalIds);
  }, [props.externalLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReassignPress = () => {
    if (props.onReassignPress) {
      props.onReassignPress();
    }
  };

  const handleSeverityChange = (severity: Severity) => {
    setSelectedSeverity(severity);
    setSeverityChanged(severity !== (props.issue?.severity ?? Severity.Red));
  };

  const typeOptions = [
    {
      label: t('activity.filter.urgent'),
      value: Severity.Red,
      iconVariant: 'attention',
    },
    {
      label: t('activity.filter.pending'),
      value: Severity.Yellow,
      iconVariant: 'blocker',
    },
    {
      label: t('activity.filter.solved'),
      value: Severity.Green,
      iconVariant: 'solved',
    },
    {
      label: t('activity.filter.note'),
      value: Severity.Purple,
      iconVariant: 'note',
    },
  ];

  const handleLabelsChange = (
    newLabels: Array<{ id: string; name: string }>
  ) => {
    setSelectedLabels(newLabels);
    const currentLabelIds = (props.currentLabels || []).map((l) => l.id).sort();
    const newLabelIds = newLabels.map((l) => l.id).sort();
    const hasChanged =
      currentLabelIds.length !== newLabelIds.length ||
      currentLabelIds.some((id, index) => id !== newLabelIds[index]);
    setLabelsChanged(hasChanged);
    props.onLabelsChange?.(newLabels);
  };

  const handleCreateLabel = async (name: string) => {
    const result = await createLabel({
      input: { name, workspace: { id: props.workspaceId } },
    });
    return result;
  };

  const createOrUpdateReminder = async (onReminderDate: string) => {
    const workspaceId = { id: props.workspaceId };
    const trigger = {
      type: 'ISSUE',
      config: {
        issueId: props.issueId,
        onDate: parseMMDDYYYYToDate(onReminderDate),
      },
    };

    const action = {
      type: ActionType.Reminder,
    };

    const newReminderInput = {
      name: '',
      workspace: workspaceId,
      trigger: trigger,
      action: action,
    };

    const editReminderInput = {
      name: '',
      trigger: trigger,
      action: action,
      workspace: workspaceId,
    };

    if (workflows?.items?.[0]) {
      await updateReminder(editReminderInput);
    } else {
      await createReminder(newReminderInput);
    }
  };

  const createReminder = async (input: Record<string, unknown>) => {
    if (!props.issue.workspace?.id) {
      return;
    }

    const workflowInput = {
      ...input,
      workspace: {
        id: props.issue.workspace.id,
      },
    };

    await createWorkflow({ input: workflowInput });
  };

  const updateReminder = async (input: Record<string, unknown>) => {
    const updateInput = {
      ...input,
      id: workflows?.items?.[0].id,
    };

    await updateWorkflow({ input: updateInput });
  };

  const onResetForm = () => {
    reset({
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
      onReminderDate: currentWorkflow?.trigger?.config?.onDate
        ? formatDateToMMDDYYYY(currentWorkflow.trigger.config.onDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    setSelectedSeverity(props.issue?.severity ?? Severity.Red);
    setSeverityChanged(false);
    setIsPinned(props.issue?.isPinned ?? false);
    setPinChanged(false);
    props.onLabelsChange?.(props.currentLabels || []);
  };

  const combinedIsDirty =
    isDirty || labelsChanged || severityChanged || pinChanged;

  useEffect(() => {
    if (!props.onFormStateChange) return;
    props.onFormStateChange(combinedIsDirty, isValid, () =>
      handleSubmit(onSubmit)()
    );
  }, [combinedIsDirty, isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePinToggle = (value: boolean) => {
    setIsPinned(value);
    setPinChanged(value !== (props.issue?.isPinned ?? false));
  };

  // For campaign-typed issues we render the form in read-only mode: title and
  // createdAt are visible but disabled; type, reassign, adjust-creation-date and
  // the save/delete affordances are hidden entirely. The host (activityEditColumn
  // / CampaignThreadPanel) decides what to render in contentFooter — this
  // component just removes the in-form editable affordances. Pin, reminder and
  // labels are an exception when the host opts in via allowCampaignMetadataEdit
  // (the campaign thread settings panel, which supplies its own save bar).
  const isCampaign = props.issue?.type === IssueType.Campaign;
  const campaignMetadataReadOnly =
    isCampaign && !props.allowCampaignMetadataEdit;

  const renderReviseSubmissionSection = () => {
    if (Platform.OS !== 'web') return null;
    if (!props.onReviseSubmissionPress) return null;

    const isTallyCounter =
      props.issue.routineVersion?.type === RoutineType.Counter &&
      (props.issue.routineVersion?.config as any)?.counterType ===
        CounterType.Tally;

    if (props.issue.type !== IssueType.Routine || isTallyCounter) return null;

    const routineTitle =
      (props.issue.routineVersion?.config as any)?.title ||
      props.issue.routineVersion?.name ||
      '';

    return (
      <View style={formStyles.formItem}>
        <Text style={formStyles.formItemTitleCropCircle}>
          {t('issue.editModal.reviseSubmission')}
        </Text>
        <CardButton
          text={routineTitle}
          leftIcon="Routine"
          rightIcon="Chevron Right"
          showAvatars={false}
          onPress={props.onReviseSubmissionPress}
          color="blue"
        />
      </View>
    );
  };

  const BottomBar = props.bottomBar;

  const modalContent = (
    <>
      {renderReviseSubmissionSection()}
      <View style={formStyles.formItem}>
        <View style={styles.pinRow}>
          <Icon name="Pin" size={20} color={colors.brand.black} />
          <View style={styles.pinContent}>
            <Text style={styles.pinTitle}>{t('issue.menu.pinToAsset')}</Text>
            <Text style={styles.pinSubtitle}>
              {t('issue.editModal.pinDescription')}
            </Text>
          </View>
          <Toggle
            value={isPinned}
            onValueChange={handlePinToggle}
            colorScheme="success"
            disabled={campaignMetadataReadOnly}
          />
        </View>
      </View>
      {props.issue.type !== IssueType.Routine && !isCampaign && (
        <View style={formStyles.formItem}>
          <View>
            <Text style={formStyles.formItemTitleCropCircle}>
              {t('issue.new.type')}
            </Text>
          </View>
          <View style={styles.typeGrid}>
            {typeOptions.map((option) => (
              <Button
                key={option.value}
                variant="rounded"
                onPress={() => handleSeverityChange(option.value)}
                leftIcon={
                  <TypeIcon variant={option.iconVariant as TypeVariant} />
                }
                state={selectedSeverity === option.value ? 'active' : 'default'}
                style={{
                  backgroundColor:
                    selectedSeverity !== option.value
                      ? colors.neutral[200]
                      : colors.neutral[800],
                }}
              >
                {option.label}
              </Button>
            ))}
          </View>
        </View>
      )}
      {!!props.workspaceId && (
        <View style={formStyles.formItem}>
          <View>
            <Text style={formStyles.formItemTitleCropCircle}>
              {t('issue.new.labels')}
            </Text>
          </View>
          <LabelSelector
            selectedLabels={selectedLabels}
            availableLabels={availableLabels}
            onLabelsChange={handleLabelsChange}
            onCreateLabel={handleCreateLabel}
            onSelectorEdit={
              campaignMetadataReadOnly ? undefined : props.onLabelSelectorEdit
            }
            disabled={campaignMetadataReadOnly}
          />
        </View>
      )}
      <View style={formStyles.formItem}>
        <View>
          <Text style={formStyles.formItemTitleCropCircle}>
            {t('issue.new.reminder')}
          </Text>
        </View>
        <DatePickerSelector
          name="onReminderDate"
          setValue={setValue}
          control={control}
          placeholder="MM/DD/YYYY"
          hideCalendarIcon
          maxYears={5}
          minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
          disabled={campaignMetadataReadOnly}
          // When read-only, match the created-date/title disabled grey instead
          // of the lighter default opacity dimming.
          disabledTextColor={tokens.colors.system.textSecondary}
        />
      </View>
      <View style={formStyles.formItem}>
        <View>
          <Text style={formStyles.formItemTitleCropCircle}>
            {t('issue.new.title')}
          </Text>
        </View>
        <ControlledTextInput
          control={control}
          name="title"
          height={50}
          placeholder={t('issue.new.optionalPlaceholder')}
          disableInnerStyle={false}
          innerRef={Platform.OS === 'web' ? elementFocusCallBack : undefined}
          backgroundColor={tokens.colors.neutral[200]}
          hideBorder={true}
          editable={!isCampaign}
          // Campaign titles are read-only; show the value in the standard muted
          // text grey rather than the lighter placeholder-grey disabled default.
          textColor={
            isCampaign ? tokens.colors.system.textSecondary : undefined
          }
        />
      </View>
      {!isCampaign && (
        <View style={formStyles.formItem}>
          <View>
            <Text style={formStyles.formItemTitleCropCircle}>
              {t('issue.new.reassign')}
            </Text>
          </View>
          <CardButton
            text={props.issue?.asset?.name ?? ''}
            showAvatars={false}
            bordered={false}
            showLeftIcon={false}
            onPress={onReassignPress}
          ></CardButton>
        </View>
      )}
      {!isCampaign && (
        <View style={formStyles.formItem}>
          <AdjustCreationDateField control={control} setValue={setValue} />
        </View>
      )}
      <View style={formStyles.formItem}>
        <CreatedDateField createdAt={props.currentCreatedAt || ''} />
      </View>
    </>
  );

  return (
    <SaveFormBottom
      isDirty={combinedIsDirty}
      isValid={isValid}
      onSubmit={handleSubmit(onSubmit)}
      title={t('issue.editModal.header')}
      saveLabel={t('issue.editModal.save')}
      onCancel={onEditCancel}
      onResetForm={onResetForm}
      bottomBar={(barProps) => (
        <BottomBar {...barProps} isDirty={combinedIsDirty} />
      )}
    >
      {Platform.OS === 'web' ? (
        <ScrollView style={styles.formContainer}>
          {modalContent}
          {props.contentFooter}
        </ScrollView>
      ) : (
        <KeyboardSafeView style={styles.keyboardContainer}>
          <ScrollView scrollEnabled showsVerticalScrollIndicator>
            {modalContent}
            {props.onDeleted && (
              <View style={styles.deleteIssueContainer}>
                <DeleteIssue
                  issueId={props.issueId}
                  onDeleted={props.onDeleted}
                />
              </View>
            )}
          </ScrollView>
        </KeyboardSafeView>
      )}
    </SaveFormBottom>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    gap: 8,
    paddingBottom: Platform.OS === 'web' ? 16 : 400,
    paddingTop: 16,
    flexGrow: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  deleteIssueContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.smallPadding.paddingVertical,
    paddingBottom: Spacing.smallPadding.paddingVertical * 8,
    backgroundColor: Colors.brand.white,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pinRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pinContent: {
    flex: 1,
  },
  pinTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.brand.black,
  },
  pinSubtitle: {
    fontSize: 12,
    color: colors.system.textSecondary,
    marginTop: 2,
  },
  mobileContainer: {
    backgroundColor: Colors.white,
    height: '100%',
  },
  inputContainer: {
    flex: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
});

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) { id name }
  }
`;

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;
